import { readProductScore } from "./tirePairDisplay";
import {
  isEuropeanStockProduct,
  isLocalStockProduct,
  sortRecommended,
} from "./tireListModel";

export const LIST_PAGE_SIZE = 24;

export const DEFAULT_LIST_FILTERS = {
  type: "all",
  stock: [],
  freeShippingOnly: false,
  brands: [],
  rims: [],
  minPrice: null,
  maxPrice: null,
  sort: "recommended",
};

const FILTER_QUERY_KEYS = ["type", "stock", "ship", "brands", "rim", "min", "max", "sort"];
const LEGACY_QUERY_KEYS = ["local", "eu", "sets", "single"];
const SORTS = ["recommended", "price_asc", "price_desc", "score_desc"];

function uniqueLower(values) {
  const seen = new Set();
  const result = [];

  values.forEach((value) => {
    const key = String(value || "").trim().toLowerCase();
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push(key);
  });

  return result;
}

function uniqueRims(values) {
  const seen = new Set();
  const result = [];

  values.forEach((value) => {
    const rim = String(value == null ? "" : value).trim();
    if (!rim || seen.has(rim)) {
      return;
    }
    seen.add(rim);
    result.push(rim);
  });

  result.sort((left, right) => {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    const leftNumeric = Number.isFinite(leftNumber);
    const rightNumeric = Number.isFinite(rightNumber);

    if (leftNumeric && rightNumeric && leftNumber !== rightNumber) {
      return leftNumber - rightNumber;
    }
    if (leftNumeric !== rightNumeric) {
      return leftNumeric ? -1 : 1;
    }
    return left.localeCompare(right, "en");
  });

  return result;
}

function normalizeStock(values) {
  const selected = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const key = String(value || "").trim().toLowerCase();
    if (key === "local" || key === "eu") {
      selected.add(key);
    }
  });

  const stock = [];
  if (selected.has("local")) {
    stock.push("local");
  }
  if (selected.has("eu")) {
    stock.push("eu");
  }
  return stock;
}

function toPrice(value) {
  if (value == null || value === "") {
    return null;
  }

  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) {
    return null;
  }

  return Math.round(number);
}

function isFlag(value) {
  return value === "1" || value === "true";
}

const TYPES = ["all", "single", "set"];

// The size page defaults to single tires; brand and category pages default to all.
function resolveDefaultType(options) {
  const type = options && options.defaultType;
  return TYPES.indexOf(type) === -1 ? "all" : type;
}

function normalizeListFilters(filters, defaultType = "all") {
  const source = filters && typeof filters === "object" ? filters : {};
  const type = TYPES.indexOf(source.type) === -1 ? defaultType : source.type;
  const sort = SORTS.indexOf(source.sort) === -1 ? "recommended" : source.sort;

  let minPrice = toPrice(source.minPrice);
  let maxPrice = toPrice(source.maxPrice);
  if (minPrice != null && maxPrice != null && minPrice > maxPrice) {
    const swap = minPrice;
    minPrice = maxPrice;
    maxPrice = swap;
  }

  return {
    type,
    stock: normalizeStock(source.stock),
    freeShippingOnly: !!source.freeShippingOnly,
    brands: uniqueLower(Array.isArray(source.brands) ? source.brands : []),
    rims: uniqueRims(Array.isArray(source.rims) ? source.rims : []),
    minPrice,
    maxPrice,
    sort,
  };
}

export function parseListFiltersFromSearch(search, options = {}) {
  const defaultType = resolveDefaultType(options);
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  const typeParam = params.get("type");
  let type = typeParam;
  if (TYPES.indexOf(type) === -1) {
    if (!typeParam && isFlag(params.get("sets"))) {
      type = "set";
    } else if (!typeParam && !isFlag(params.get("sets")) && isFlag(params.get("single"))) {
      type = "single";
    } else {
      type = defaultType;
    }
  }

  const stock = (params.get("stock") || "").split(",");
  if (isFlag(params.get("local"))) {
    stock.push("local");
  }
  if (isFlag(params.get("eu"))) {
    stock.push("eu");
  }

  return normalizeListFilters({
    type,
    stock,
    freeShippingOnly: params.get("ship") === "free",
    brands: (params.get("brands") || "").split(","),
    rims: (params.get("rim") || "").split(","),
    minPrice: params.get("min"),
    maxPrice: params.get("max"),
    sort: params.get("sort"),
  }, defaultType);
}

export function getSearchForListFilters(search, filters, options = {}) {
  const defaultType = resolveDefaultType(options);
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  const normalized = normalizeListFilters(filters, defaultType);

  FILTER_QUERY_KEYS.concat(LEGACY_QUERY_KEYS).forEach((key) => params.delete(key));

  if (normalized.type !== defaultType) {
    params.set("type", normalized.type);
  }
  if (normalized.stock.length > 0) {
    params.set("stock", normalized.stock.join(","));
  }
  if (normalized.freeShippingOnly) {
    params.set("ship", "free");
  }
  if (normalized.brands.length > 0) {
    params.set("brands", normalized.brands.join(","));
  }
  if (normalized.rims.length > 0) {
    params.set("rim", normalized.rims.join(","));
  }
  if (normalized.minPrice != null) {
    params.set("min", String(normalized.minPrice));
  }
  if (normalized.maxPrice != null) {
    params.set("max", String(normalized.maxPrice));
  }
  if (normalized.sort !== "recommended") {
    params.set("sort", normalized.sort);
  }

  const next = params.toString();
  return next ? `?${next}` : "";
}

function stableSort(list, compare) {
  return list
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const delta = compare(left.item, right.item);
      if (delta !== 0 && !Number.isNaN(delta)) {
        return delta;
      }
      return left.index - right.index;
    })
    .map((entry) => entry.item);
}

function priceOf(product) {
  const value = Number(product && product.price);
  return Number.isFinite(value) ? value : 0;
}

export function applyListFilters(products, filters, options = {}) {
  const list = Array.isArray(products) ? products.slice() : [];
  const normalized = normalizeListFilters(filters);
  const selectedBrands = new Set(normalized.brands);
  const selectedRims = new Set(normalized.rims);
  const selectedStock = normalized.stock;
  const limitedOfferEanSet = options.limitedOfferEanSet;

  const filtered = list.filter((product) => {
    if (normalized.type === "single" && product && product.set) {
      return false;
    }
    if (normalized.type === "set" && !(product && product.set)) {
      return false;
    }
    if (selectedStock.length > 0) {
      const matchesLocal = selectedStock.indexOf("local") !== -1 && isLocalStockProduct(product);
      const matchesEu = selectedStock.indexOf("eu") !== -1 && isEuropeanStockProduct(product);
      if (!matchesLocal && !matchesEu) {
        return false;
      }
    }
    if (normalized.freeShippingOnly && !(product && product.free_shipping === true)) {
      return false;
    }
    if (selectedBrands.size > 0) {
      const brand = String(product && product.brand).trim().toLowerCase();
      if (!selectedBrands.has(brand)) {
        return false;
      }
    }
    if (selectedRims.size > 0) {
      const rim = String(product && product.rim != null ? product.rim : "").trim();
      if (!selectedRims.has(rim)) {
        return false;
      }
    }

    const price = Number(product && product.price);
    if (normalized.minPrice != null && price < normalized.minPrice) {
      return false;
    }
    if (normalized.maxPrice != null && price > normalized.maxPrice) {
      return false;
    }

    return true;
  });

  const recommended = sortRecommended(filtered, limitedOfferEanSet);
  if (normalized.sort === "price_asc" || normalized.sort === "price_desc") {
    const direction = normalized.sort === "price_desc" ? -1 : 1;
    return stableSort(recommended, (left, right) => (priceOf(left) - priceOf(right)) * direction);
  }
  if (normalized.sort === "score_desc") {
    return stableSort(recommended, (left, right) => {
      const leftScore = readProductScore(left);
      const rightScore = readProductScore(right);
      if (leftScore == null && rightScore == null) {
        return 0;
      }
      if (leftScore == null) {
        return 1;
      }
      if (rightScore == null) {
        return -1;
      }
      return rightScore - leftScore;
    });
  }

  return recommended;
}

function isAllUpperCase(value) {
  const text = String(value || "");
  return text === text.toUpperCase() && text !== text.toLowerCase();
}

function preferBrandLabel(current, next) {
  if (!current) {
    return next;
  }
  if (isAllUpperCase(current) && !isAllUpperCase(next)) {
    return next;
  }
  return current;
}

// Some EU feed sizes ("2.75/ 10 37J") arrive with the load index in `rim`.
// Keep those products listed, but don't offer impossible rim sizes as filters.
function isPlausibleRim(rim) {
  if (!/^\d{1,2}$/.test(rim)) {
    return false;
  }
  const value = Number(rim);
  return value >= 8 && value <= 24;
}

export function getListFacets(products) {
  const list = Array.isArray(products) ? products : [];
  const brandCounts = new Map();
  const rimCounts = new Map();
  let rawMin = null;
  let rawMax = null;
  let singleCount = 0;
  let setCount = 0;
  let localCount = 0;
  let euCount = 0;
  let freeShippingCount = 0;

  list.forEach((product) => {
    const brand = String(product && product.brand != null ? product.brand : "").trim();
    if (brand) {
      const key = brand.toLowerCase();
      const prev = brandCounts.get(key);
      brandCounts.set(key, {
        key,
        label: preferBrandLabel(prev && prev.label, brand),
        count: prev ? prev.count + 1 : 1,
      });
    }

    const rim = String(product && product.rim != null ? product.rim : "").trim();
    if (isPlausibleRim(rim)) {
      rimCounts.set(rim, (rimCounts.get(rim) || 0) + 1);
    }

    const price = Number(product && product.price);
    if (Number.isFinite(price)) {
      rawMin = rawMin == null ? price : Math.min(rawMin, price);
      rawMax = rawMax == null ? price : Math.max(rawMax, price);
    }

    if (product && product.set) {
      setCount += 1;
    } else {
      singleCount += 1;
    }
    if (isLocalStockProduct(product)) {
      localCount += 1;
    }
    if (isEuropeanStockProduct(product)) {
      euCount += 1;
    }
    if (product && product.free_shipping === true) {
      freeShippingCount += 1;
    }
  });

  const brands = Array.from(brandCounts.values()).sort((left, right) => {
    if (right.count !== left.count) {
      return right.count - left.count;
    }
    return left.label.localeCompare(right.label, "he");
  });

  const rims = uniqueRims(Array.from(rimCounts.keys())).map((rim) => ({
    rim,
    count: rimCounts.get(rim),
  }));

  let priceMin = null;
  let priceMax = null;
  if (rawMin != null) {
    if (rawMin === rawMax) {
      priceMin = Math.round(rawMin);
      priceMax = priceMin;
    } else {
      priceMin = Math.floor(rawMin);
      priceMax = Math.ceil(rawMax);
    }
  }

  return {
    totalCount: list.length,
    singleCount,
    setCount,
    localCount,
    euCount,
    freeShippingCount,
    brands,
    rims,
    priceMin,
    priceMax,
  };
}

export function getVisibleListFilterSections(facets) {
  const source = facets && typeof facets === "object" ? facets : {};
  const brands = Array.isArray(source.brands) ? source.brands : [];
  const rims = Array.isArray(source.rims) ? source.rims : [];
  const totalCount = source.totalCount || 0;
  const sections = {
    type: source.singleCount > 0 && source.setCount > 0,
    stock:
      (source.localCount > 0 && source.localCount < totalCount) ||
      (source.euCount > 0 && source.euCount < totalCount),
    freeShipping: source.freeShippingCount > 0 && source.freeShippingCount < totalCount,
    price:
      source.priceMin != null &&
      source.priceMax != null &&
      source.priceMin !== source.priceMax,
    rims: rims.length > 1,
    brands: brands.length > 1,
  };

  sections.any =
    sections.type ||
    sections.stock ||
    sections.freeShipping ||
    sections.price ||
    sections.rims ||
    sections.brands;
  return sections;
}

// A page default of "single" or "set" falls back to "all" when that type has
// no products, so the default view is never empty.
export function getEffectiveDefaultType(defaultType, facets) {
  const type = resolveDefaultType({ defaultType });
  const source = facets && typeof facets === "object" ? facets : {};
  if (type === "single" && !(source.singleCount > 0)) {
    return "all";
  }
  if (type === "set" && !(source.setCount > 0)) {
    return "all";
  }
  return type;
}

export function countActiveListFilters(filters, options = {}) {
  const defaultType = resolveDefaultType(options);
  const normalized = normalizeListFilters(filters, defaultType);
  let count = normalized.stock.length + normalized.brands.length + normalized.rims.length;

  if (normalized.type !== defaultType) {
    count += 1;
  }
  if (normalized.freeShippingOnly) {
    count += 1;
  }
  if (normalized.minPrice != null || normalized.maxPrice != null) {
    count += 1;
  }

  return count;
}
